import React, { useCallback, useEffect, useState } from 'react';
import { History, ShoppingBag, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { useLiveRefresh } from '../../hooks/useLiveRefresh';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Phase 4 — role-aware financial activity timeline + marketplace footprint,
 * engine-sourced via /api/finance/profile. Live-synced. */
export default function FinancialTimeline() {
  const [profile, setProfile] = useState(null);

  const load = useCallback(() => {
    apiCall('/api/finance/profile')
      .then((d) => setProfile(d.profile))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useLiveRefresh(['finance', 'wallet', 'payroll', 'marketplace'], load);

  if (!profile) return null;
  const { marketplace, timeline } = profile;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid="financial-timeline">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          <History className="w-4 h-4 text-indigo-600" /> Financial Activity
        </h3>
        <span className="text-[11px] text-slate-400 flex items-center gap-1" data-testid="timeline-marketplace-stats">
          <ShoppingBag className="w-3 h-3" />
          Bought {marketplace.purchases_count} ({money(marketplace.purchases_total)}) · Sold {marketplace.sales_count} ({money(marketplace.sales_total)})
        </span>
      </div>
      <div className="divide-y divide-slate-50 max-h-72 overflow-y-auto">
        {timeline.length === 0 ? (
          <p className="px-4 py-6 text-xs text-slate-400 text-center">No financial activity yet.</p>
        ) : timeline.map((e) => (
          <div key={e._id} className="px-4 py-2 flex items-center gap-3 text-xs" data-testid="timeline-event">
            {e.direction === 'credit'
              ? <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              : e.direction === 'debit'
                ? <ArrowUpRight className="w-3.5 h-3.5 text-red-400 shrink-0" />
                : <History className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="text-slate-700 truncate">{e.description || e.event_type}</p>
              <p className="text-[10px] text-slate-400">{e.event_type} · {e.created_at ? new Date(e.created_at).toLocaleString() : ''}</p>
            </div>
            <span className={`font-semibold ${e.direction === 'credit' ? 'text-emerald-600' : e.direction === 'debit' ? 'text-red-500' : 'text-slate-500'}`}>
              {e.direction === 'debit' ? '-' : e.direction === 'credit' ? '+' : ''}{money(e.amount)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
